<template>
  <div class="min-h-screen flex flex-col relative z-10">
    <background-patterns />

    <header
      class="app-header fixed inset-x-0 top-0 z-50 bg-white/85 backdrop-blur border-b border-black/5"
    >
      <div class="max-w-8xl mx-auto px-3 sm:px-6 py-2 sm:py-3">
        <div class="flex items-center justify-between">
          <router-link
            to="/"
            class="flex items-center h-8 sm:h-10 hover:scale-105 transition-transform"
          >
            <img
              :src="systemLogo"
              alt="Aberdeen Institute"
              class="h-[85%] sm:h-[90%] w-auto object-contain object-scale-down"
            />
          </router-link>

          <div class="flex items-center gap-1 sm:gap-2">
            <StatusIndicator class="hidden lg:inline-flex" />
            <LanguageSelector />
            <UserInfo />
          </div>
        </div>
      </div>
    </header>

    <main
      class="app-main flex-1 flex justify-center items-center px-3 sm:px-6 mt-14 sm:mt-16 relative z-10"
      :class="{
        'about-page': isAboutPage,
        'status-page': isStatusPage,
      }"
    >
      <div
        v-if="!isAboutPage && !isStatusPage"
        class="w-full bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-card p-4 sm:p-6 md:p-8 transition-all duration-500 ease-out transform translate-y-4 opacity-0"
        :class="{
          'translate-y-0 opacity-100': initialized,
          'max-w-[480px]': !fullWidth,
          'max-w-[960px]': fullWidth,
        }"
      >
        <slot />
      </div>

      <div
        v-else
        class="w-full transition-all duration-500 ease-out transform translate-y-4 opacity-0"
        :class="{ 'translate-y-0 opacity-100': initialized }"
      >
        <slot />
      </div>
    </main>

    <footer
      class="app-footer mt-auto relative z-10"
      :class="{
        'about-footer': isAboutPage,
        'status-footer': isStatusPage,
      }"
    >
      <div class="mx-auto max-w-8xl px-3 sm:px-6 py-6 sm:py-8 lg:px-8">
        <div class="border-t border-gray-900/10 pt-6 sm:pt-8">
          <div class="flex flex-col items-center gap-4 sm:gap-6">
            <button
              class="lg:hidden inline-flex items-center gap-x-1.5 sm:gap-x-2 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium transition-all"
              :class="[footerStatusClass]"
              @click="goToStatus"
            >
              <component
                :is="statusIcon"
                class="h-4 w-4 sm:h-5 sm:w-5"
                :class="{ 'animate-spin': status === 'loading' }"
              />
              <span>{{ statusText }}</span>
            </button>

            <div
              class="flex flex-col sm:flex-row items-center sm:items-start justify-between w-full gap-4 sm:gap-0"
            >
              <div
                class="flex flex-col sm:flex-row items-center sm:items-start gap-4"
              >
                <p
                  class="text-xs sm:text-sm text-center sm:text-left text-gray-45"
                >
                  &copy; {{ currentYear }}
                  {{
                    t("footer.institute", {
                      brandFullName: brandConfig.brandFullName,
                    })
                  }}.
                  {{ t("footer.rights") }}
                </p>
                <div
                  class="flex items-center gap-4 text-xs sm:text-sm text-gray-45"
                >
                  <router-link
                    to="/about"
                    class="hover:text-gray-600 transition-colors"
                  >
                    {{ t("about.title") }}
                  </router-link>
                  <span class="hidden sm:inline text-gray-300">|</span>
                  <router-link
                    to="/status"
                    class="hover:text-gray-600 transition-colors"
                  >
                    {{ t("footer.status") }}
                  </router-link>
                </div>
              </div>

              <VersionDisplay />
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useBrandConfig } from "@/utils/brand";
import { useRouter } from "vue-router";
import { systemConfigService } from "@/services/systemConfigService";
import { pathEquals } from "@/utils/path";
import UserInfo from "@/components/common/UserInfo.vue";
import LanguageSelector from "@/components/common/LanguageSelector.vue";
import BackgroundPatterns from "./BackgroundPatterns.vue";
import StatusIndicator from "@/components/common/StatusIndicator.vue";
import VersionDisplay from "@/components/common/VersionDisplay.vue";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  WrenchIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon,
} from "@heroicons/vue/24/outline";

type SystemStatus =
  | "all_operational"
  | "partial_down"
  | "majority_down"
  | "all_down"
  | "maintenance"
  | "unknown"
  | "loading";

const { t } = useI18n();
const brandConfig = useBrandConfig();
const router = useRouter();
defineProps({
  fullWidth: {
    type: Boolean,
    default: false,
  },
});

// Path comparisons using normalized path equality
const isAboutPage = computed(() =>
  pathEquals(router.currentRoute.value.path, "/about"),
);
const isStatusPage = computed(() =>
  pathEquals(router.currentRoute.value.path, "/status"),
);

const status = ref<SystemStatus>("unknown");
const statusInterval = ref<ReturnType<typeof setInterval> | null>(null);

const fetchStatus = async () => {
  try {
    const response = await fetch("/status/api/status-page/heartbeat/abdnims");
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    const heartbeatList = data.heartbeatList;

    const statuses = Object.keys(heartbeatList).map((id) => {
      const beats = heartbeatList[id];
      return beats.length > 0 ? beats[beats.length - 1].status : 2;
    });

    if (statuses.length === 0) {
      status.value = "unknown";
      return;
    }

    if (statuses.every((s) => s === 1)) {
      status.value = "all_operational";
      return;
    }

    if (statuses.every((s) => s === 0)) {
      status.value = "all_down";
      return;
    }

    if (statuses.some((s) => s === 3)) {
      status.value = "maintenance";
      return;
    }

    const downCount = statuses.filter((s) => s === 0).length;
    const totalCount = statuses.length;

    if (downCount > totalCount / 2) {
      status.value = "majority_down";
      return;
    }

    if (downCount > 0) {
      status.value = "partial_down";
      return;
    }

    status.value = "unknown";
  } catch (error) {
    console.error("Error fetching status:", error);
    status.value = "unknown";
  }
};

const statusIcon = computed(() => {
  switch (status.value) {
    case "all_operational":
      return CheckCircleIcon;
    case "partial_down":
      return ExclamationTriangleIcon;
    case "majority_down":
      return ExclamationCircleIcon;
    case "all_down":
      return XCircleIcon;
    case "maintenance":
      return WrenchIcon;
    case "loading":
      return ArrowPathIcon;
    default:
      return QuestionMarkCircleIcon;
  }
});

const statusText = computed(() => {
  switch (status.value) {
    case "all_operational":
      return t("status.allOperational");
    case "partial_down":
      return t("status.partialDown");
    case "majority_down":
      return t("status.majorityDown");
    case "all_down":
      return t("status.allDown");
    case "maintenance":
      return t("status.maintenance");
    default:
      return t("status.unknown");
  }
});

const statusClasses = {
  all_operational:
    "bg-green-50/80 text-green-600 ring-1 ring-green-500/10 hover:bg-green-100/80 hover:ring-green-500/20",
  partial_down:
    "bg-yellow-50/80 text-yellow-600 ring-1 ring-yellow-500/10 hover:bg-yellow-100/80 hover:ring-yellow-500/20",
  majority_down:
    "bg-yellow-50/80 text-yellow-600 ring-1 ring-yellow-500/10 hover:bg-yellow-100/80 hover:ring-yellow-500/20",
  all_down:
    "bg-red-50/80 text-red-600 ring-1 ring-red-500/10 hover:bg-red-100/80 hover:ring-red-500/20",
  maintenance:
    "bg-blue-50/80 text-blue-600 ring-1 ring-blue-500/10 hover:bg-blue-100/80 hover:ring-blue-500/20",
  unknown:
    "bg-gray-50/80 text-gray-600 ring-1 ring-gray-200/20 hover:bg-gray-100/80 hover:ring-gray-300/30",
  loading:
    "bg-gray-50/80 text-gray-600 ring-1 ring-gray-200/20 hover:bg-gray-100/80 hover:ring-gray-300/30",
} as const;

const footerStatusClass = computed(() => {
  return statusClasses[status.value];
});

const goToStatus = () => {
  router.push("/status");
};

const currentYear = computed(() => new Date().getFullYear());
const initialized = ref(false);

// 获取系统logo路径
const systemLogo = computed(() => {
  return systemConfigService.systemLogo;
});

onMounted(() => {
  setTimeout(() => {
    initialized.value = true;
  }, 100);

  status.value = "loading";
  fetchStatus();
  statusInterval.value = setInterval(fetchStatus, 5000);
});

onUnmounted(() => {
  if (statusInterval.value) {
    clearInterval(statusInterval.value);
    statusInterval.value = null;
  }
});
</script>

<style>
:root {
  --app-header-h-mobile: 56px;
  --app-header-h-desktop: 64px;
  --app-footer-h-mobile: 88px;
  --app-footer-h-desktop: 112px;
}

@media (min-width: 640px) {
  :root {
    --app-header-h-mobile: 64px;
    --app-footer-h-mobile: 96px;
  }
}

@media (min-width: 1024px) {
  :root {
    --app-header-h-desktop: 64px;
    --app-footer-h-desktop: 120px;
  }
}

.app-header {
  height: auto;
}

.app-main {
  min-height: calc(
    100vh - var(--header-effective-h) - var(--footer-effective-h)
  );
}

@media (max-width: 1023.98px) {
  :root {
    --header-effective-h: var(--app-header-h-mobile);
    --footer-effective-h: var(--app-footer-h-mobile);
  }
}

@media (min-width: 1024px) {
  :root {
    --header-effective-h: var(--app-header-h-desktop);
    --footer-effective-h: var(--app-footer-h-desktop);
  }
}

.about-page {
  @apply px-0;
}

.status-page {
  @apply px-0;
}

.about-footer {
  @apply bg-white/95 backdrop-blur-xl border-t border-black/5;
}

.status-footer {
  @apply bg-white/95 backdrop-blur-xl border-t border-black/5;
}
</style>
